/**
 * telemetrySentry — Adapter do provider Sentry para o facade de telemetria.
 *
 * Mantém o app 100% desacoplado do SDK: nenhum outro arquivo importa
 * `@sentry/react-native`. Aqui inicializamos o Sentry e registramos um provider
 * no facade (`src/services/telemetry.js`), que já é chamado em todo o app via
 * `captureException` / `captureMessage` / `setTelemetryUser`.
 *
 * Ativação por ambiente (degrada com segurança):
 *   - Sem DSN  → não inicializa (facade segue no-op em PROD / console em DEV).
 *   - __DEV__  → não inicializa (DEV já espelha no console; evita ruído/quota).
 *   - PROD com DSN (builds preview/production) → Sentry ativo.
 *
 * Como ligar: definir `EXPO_PUBLIC_SENTRY_DSN` no `.env` (ou `expo.extra.sentryDsn`
 * no app.json) e gerar um build (o módulo nativo do Sentry não roda no Expo Go).
 */
import Constants from 'expo-constants';
import { Platform } from 'react-native';

import { registerTelemetryProvider } from './telemetry';

const resolveDsn = () =>
    process.env.EXPO_PUBLIC_SENTRY_DSN ||
    Constants.expoConfig?.extra?.sentryDsn ||
    null;

let initialized = false;
let navigationIntegration = null;
// Referência ao SDK já carregado (na web ele chega de forma assíncrona).
let sentryModule = null;
// Container de navegação que pediu registro ANTES de o SDK chegar (web).
let pendingNavigationRef = null;

const toSentryCaptureContext = (context) => {
    if (!context) return undefined;

    const { tags, extra, level, fingerprint, ...operationalContext } = context;
    return {
        ...(tags ? { tags } : {}),
        ...(level ? { level } : {}),
        ...(fingerprint ? { fingerprint } : {}),
        extra: {
            ...operationalContext,
            ...(extra || {}),
        },
    };
};

/**
 * Inicializa o Sentry e registra o provider no facade. Idempotente e seguro:
 * qualquer falha é engolida (telemetria nunca deve derrubar o boot).
 */
/**
 * Monta o Sentry a partir do módulo já carregado e registra o provider no facade.
 * Extraído do `initTelemetry` porque a web passou a carregar o SDK de forma
 * assíncrona — o corpo da configuração é idêntico nas duas plataformas.
 */
const setupSentry = (Sentry, dsn) => {
    if (initialized || !Sentry) return;
    try {
        sentryModule = Sentry;
        navigationIntegration = Sentry.reactNavigationIntegration?.() || null;

        const version = Constants.expoConfig?.version || 'unknown';
        const build = Platform.OS === 'web'
            ? 'web'
            : Constants.expoConfig?.ios?.buildNumber
                || Constants.expoConfig?.android?.versionCode?.toString()
                || 'unknown';

        Sentry.init({
            dsn,
            environment: 'production',
            release: `uniquest@${version}`,
            dist: String(build),

            // A API anterior (`ReactNavigationInstrumentation`) não existe no
            // SDK 7.2 instalado. Sem registrar a integração moderna, o projeto
            // recebia erros, mas nenhuma transaction de tela/performance.
            integrations: navigationIntegration
                ? (defaultIntegrations) => [...defaultIntegrations, navigationIntegration]
                : undefined,

            // ── Performance Monitoring ────────────────────────────────────────
            // Captura 25% das sessões web e 10% das sessões nativas.
            // Permite ver quais telas abrem devagar, queries lentas e gargalos.
            // A amostra web temporariamente maior dá visibilidade suficiente do
            // problema móvel sem registrar 100% das sessões nem estourar quota.
            tracesSampleRate: Platform.OS === 'web' ? 0.25 : 0.1,

            // Registra falhas HTTP 5xx no browser. Timeouts e erros de transporte
            // do Supabase também são capturados no wrapper de fetch com o path
            // sanitizado, pois requests sem resposta não têm status HTTP.
            enableCaptureFailedRequests: true,

            // ── Sessões (Crash Free Sessions / Users) ─────────────────────────
            // Habilita as métricas "Crash Free Sessions" e "Crash Free Users"
            // no dashboard — os cards que mostravam "Start Setup" passam a ter dados.
            enableAutoSessionTracking: true,
            sessionTrackingIntervalMillis: 30000,

            // ── Stack Trace em mensagens ──────────────────────────────────────
            // captureMessage() passa a incluir stack trace — sem isso as
            // mensagens chegam cegas (ex: "Realtime channel degraded" sem saber
            // de onde foi chamado no código).
            attachStacktrace: true,

            // ── Breadcrumbs ───────────────────────────────────────────────────
            // Aumenta o histórico de ações antes de um erro (padrão é 100).
            // Com 150 você vê mais do caminho que o usuário percorreu.
            maxBreadcrumbs: 150,

            // ── Enriquecimento global de eventos ──────────────────────────────
            // Adiciona contexto extra a TODOS os eventos antes de enviar:
            // versão do app, plataforma, build number — facilita filtrar no dashboard.
            beforeSend(event) {
                try {
                    event.tags = event.tags || {};
                    event.tags['app.version'] = Constants.expoConfig?.version || 'unknown';
                    event.tags['app.build'] = String(build);
                    event.tags['app.platform'] = Platform.OS;
                } catch {
                    // enriquecimento nunca pode bloquear envio
                }
                return event;
            },
        });

        registerTelemetryProvider({
            captureException: (error, context) =>
                Sentry.captureException(error, toSentryCaptureContext(context)),
            captureMessage: (message, context) =>
                Sentry.captureMessage(message, toSentryCaptureContext(context)),
            setUser: (user) => Sentry.setUser(user),
        });

        initialized = true;

        // Um `onReady` do NavigationContainer pode ter acontecido enquanto o SDK
        // ainda vinha pela rede (web). Aplica agora, senão as transactions de
        // tela nunca seriam registradas nessa sessão.
        if (pendingNavigationRef) {
            navigationIntegration?.registerNavigationContainer?.(pendingNavigationRef);
            pendingNavigationRef = null;
        }
    } catch {
        // Falha ao iniciar telemetria nunca propaga.
    }
};

/**
 * O `import()` devolve um namespace; `@sentry/react-native` é CJS, então o SDK
 * pode vir na raiz ou em `.default` conforme o interop. Resolve os dois.
 */
const resolveSentryModule = (mod) => {
    if (!mod) return null;
    if (typeof mod.init === 'function') return mod;
    if (mod.default && typeof mod.default.init === 'function') return mod.default;
    return mod;
};

/**
 * Inicializa o Sentry e registra o provider no facade. Idempotente e seguro:
 * qualquer falha é engolida (telemetria nunca deve derrubar o boot).
 */
export const initTelemetry = () => {
    if (initialized) return;
    const dsn = resolveDsn();
    // Sem DSN ou em DEV: não há provider; o facade degrada sozinho.
    if (!dsn || __DEV__) return;

    // NATIVO: caminho inalterado. O bundle já está no aparelho, então carregar o
    // SDK no boot não custa rede — e crash de inicialização é o mais importante
    // de reportar. Não há ganho em adiar.
    if (Platform.OS !== 'web') {
        try {
            setupSentry(require('@sentry/react-native'), dsn);
        } catch {
            // Falha ao iniciar telemetria nunca propaga.
        }
        return;
    }

    // WEB: `require` é resolvido ESTATICAMENTE pelo Metro. O comentário antigo
    // ("import tardio: só carrega o SDK quando realmente vamos inicializar")
    // valia para a AVALIAÇÃO, não para o EMPACOTAMENTO — medido no source map,
    // o SDK respondia por 773,7 KB crus (22,4%) do bundle de entrada, baixados
    // em toda visita fria antes de qualquer pixel. `import()` vira chunk à parte.
    //
    // Agendado em idle (mesmo padrão do pré-aquecimento de rotas no App.js) para
    // não disputar rede com o bundle e as primeiras consultas. A janela entre o
    // boot e a chegada do chunk é coberta: o facade enfileira o que for capturado
    // (ver `telemetry.js`) e o `index.js` instala handlers globais provisórios.
    const load = () => {
        import('@sentry/react-native')
            .then((mod) => setupSentry(resolveSentryModule(mod), dsn))
            .catch(() => {
                // Telemetria indisponível nunca pode derrubar o app.
            });
    };
    const idle = typeof window !== 'undefined' && window.requestIdleCallback;
    if (idle) window.requestIdleCallback(load, { timeout: 4000 });
    else setTimeout(load, 2500);
};

/** Registra o container no tracing moderno do React Navigation/Sentry. */
export const registerSentryNavigationContainer = (navigationRef) => {
    if (!navigationIntegration) {
        // Na web o SDK pode ainda estar a caminho: guarda para o `setupSentry`
        // aplicar quando chegar. Sem isto, o `onReady` acontece uma única vez e
        // a sessão inteira ficaria sem transactions de tela.
        pendingNavigationRef = navigationRef;
        return;
    }
    navigationIntegration.registerNavigationContainer?.(navigationRef);
};

/**
 * Retorna a instância do Sentry para integrações avançadas (ex: React Navigation).
 * Retorna null se ainda não inicializado ou em DEV.
 */
export const getSentryInstance = () => {
    // Devolve a referência guardada no `setupSentry`. NÃO usar `require` aqui:
    // o Metro o resolveria estaticamente e traria o SDK inteiro de volta para o
    // bundle de entrada da web, anulando o carregamento tardio.
    if (!initialized) return null;
    return sentryModule;
};

export default { initTelemetry, getSentryInstance, registerSentryNavigationContainer };
